import { useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import CosmicBackground from "../components/CosmicBackground";
import mockupReports from "../assets/mockup-personalized-reports.webp";
import mockupIntro from "../assets/mockup-aura-intro.webp";
import mockupVoice from "../assets/mockup-voice-chat.webp";
import heroBaba from "../assets/kundli-hero-baba.webp";
import personA from "../assets/in-person-woman-30s.webp";
import personB from "../assets/in-person-man-30s.webp";
import personC from "../assets/in-person-woman-20s.webp";
import "./BharatPage.css";

/**
 * /kundli, the FREE-KUNDLI campaign landing page. Cloned from /bharat on
 * Dhruv's call (2026-10-29): exact same format, content re-aimed at the
 * free kundli product, and the hero artwork holds a janam kundli patra
 * instead of the phone.
 *
 * Sibling of /foreign, same shape, different market. What changes:
 *
 *   - NO TAROT. Tarot is the foreign hook; India arrives for kundli, dasha,
 *     grah dosha and muhurat, so the feature list and the copy lead there.
 *   - the hero is an Indian jyotishi rather than a Western user.
 *   - the language is Indian English with the Hindi terms people actually
 *     search, written so it reads naturally rather than as translated copy.
 *
 * NOT GEO-GATED, deliberately. /foreign sits behind <InternationalRoute>
 * because a foreign pitch is wrong for an Indian visitor. The reverse is not
 * symmetric: this page is also the right page for the Indian diaspora abroad,
 * who are a real part of the audience, so gating it to IN-only would turn them
 * away from the page written for them.
 *
 * NOT INDEXED, matching /foreign: routed and prerendered, but absent from
 * INDEXABLE, from ALLOW in public/index.html and from the sitemap, so the
 * bootstrap stamps noindex,follow. To index it later, add it to all of those
 * lists together.
 *
 * THE DOWNLOAD BUTTON routing is copied from public/go.html, same as
 * /foreign, so all three behave identically:
 *
 *     Android  -> Play Store
 *     iOS      -> "/" (there is no iOS app)
 *     Desktop  -> "/"
 *
 * The Windows Phone exclusion carries over: its UA historically contained
 * "Android" and must not be sent to Play.
 */

const PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.astroaura.auraai";

/* The twelve RASHIS in Devanagari, Mesha through Meena.

   The /foreign page rings carry the Western glyphs (U+2648..U+2653); this is
   the India page, so it carries the names its audience actually uses. Written
   out rather than transliterated: a reader who knows jyotish reads these at a
   glance, and as a backdrop they are doing the same job the glyphs do over
   there. Decorative only, which is why the <svg> is aria-hidden. */
const RASHIS = [
  "मेष", "वृषभ", "मिथुन",
  "कर्क", "सिंह", "कन्या",
  "तुला", "वृश्चिक", "धनु",
  "मकर", "कुम्भ", "मीन",
];

/* The four things people actually arrive asking about. Shown as a divided row
   rather than a sentence: "shaadi, career, paisa ya samay ka sawaal ho" read
   as a run-on line under the headline, and a row of equal terms is both
   tidier and faster to scan. */
const INTENTS = ["Lagna", "Dasha", "Dosha", "Milan"];

const isAndroidDevice = (): boolean => {
  const ua = navigator.userAgent || navigator.vendor || "";
  return /android/i.test(ua) && !/windows phone/i.test(ua);
};

/* What the app does, framed for India. Every one of these is a real feature
   with a page behind it, so none of it is aspirational copy.

   NO TAROT HERE ON PURPOSE. Tarot is the /foreign hook. */
const FEATURES = [
  { icon: "\u{1F4DC}", label: "Free janam kundli", note: "Date, samay aur jagah se, 2 minute mein" },
  { icon: "\u{1FA90}", label: "Grahon ki sthiti", note: "Lagna aur sabhi grah, asli astronomy data se" },
  { icon: "\u{1F4AB}", label: "Dasha timeline", note: "Kaunsi mahadasha chal rahi hai, aur kab tak" },
  { icon: "\u{1F9FF}", label: "Grah dosha check", note: "Mangal, Kaal Sarp, Sade Sati, kundli se seedha" },
  { icon: "\u{1F49E}", label: "Kundli milan", note: "36 guna ka milan, doshon ke cancellation ke saath" },
  { icon: "\u{1F3A4}", label: "Kundli par sawaal", note: "AI jyotishi se Hindi mein poochiye, pehla free" },
];

/* In-app feedback from Aura AI users, VERBATIM.

   THE NAME ON A CARD MUST BE THE PERSON WHO WROTE THAT TEXT. Never invent a
   reviewer and never move one person's words onto another name: a testimonial
   from someone who did not say it is what the FTC and the ASCI codes both
   penalise. No dates are shown because none were supplied; do not fill them
   in. */
const REVIEWS = [
  {
    name: "Vicky Patil",
    stars: 5,
    text:
      "I had a very positive experience with Aura AI Astrology. The insights and guidance provided were interesting, accurate and easy to understand. The app is user-friendly and gives a unique perspective on astrology.",
  },
  {
    name: "Bhavik Patel",
    stars: 5,
    text:
      "I really love the app a lot it gives very accurate answers with detail, helps a lot because in that manner you can relate the readings with your life way more easily because it tells you when something is going to happen, how it's gonna happen in what period of your life etc.",
  },
  {
    name: "Meenu Tuteja",
    stars: 5,
    text:
      "Astro Aura gives clear and helpful astrology guidance using AI. The predictions feel accurate and easy to understand. User interface is easy to use, fast, and provides useful insights.",
  },
];

const KundliPage = () => {
  const navigate = useNavigate();

  const handleDownload = useCallback(() => {
    if (isAndroidDevice()) {
      // Leaving the site entirely, so a full assignment rather than the router.
      window.location.href = PLAY_URL;
      return;
    }
    // iOS and desktop: no app to install, so carry them into the product.
    navigate("/");
  }, [navigate]);

  return (
    <div className="in-root">
      <CosmicBackground />
      <div className="in-sky" aria-hidden="true">
        <div className="in-sky__far" />
        <div className="in-sky__mid" />
        <div className="in-sky__near" />
      </div>

      <Helmet>
        <title>Aura AI: Free Kundli, Puri Janam Patri 2 Minute Mein</title>
        <meta
          name="description"
          content="Janam date, samay aur jagah daaliye aur puri kundli turant paiye: lagna, grahon ki sthiti, dasha aur dosha. Kundli bilkul free, pehla sawaal bhi free."
        />
      </Helmet>

      <main className="in-main">
        <section className="in-hero">
          <div className="in-stage">
            <span className="in-halo" aria-hidden="true" />

            {/* The backdrop. A NORTH INDIAN KUNDLI CHART, the square-with-a
                -diamond janam patri diagram, drawn behind concentric rings
                carrying the twelve rashis. This is the India equivalent of the
                zodiac wheel on /foreign: same job, but the figure an Indian
                visitor actually recognises as their own chart.

                Drawn as SVG rather than shipped as an image, so it scales to
                any width for free. The chart and circles are STATIC; only the
                rashi ring turns, because rotating hairline strokes shimmers
                for no visible gain. */}
            <svg
              className="in-rings"
              viewBox="0 0 400 400"
              aria-hidden="true"
              focusable="false"
            >
              {/* ONLY the outer rings. The kundli chart (square, diamond and
                  corner diagonals) and the inner circle used to sit here, but
                  every one of them falls inside his silhouette radius, so they
                  drew straight across him and across the scene plate. The
                  backdrop behind him is the artwork's job; this layer stays
                  outside it. */}
              <g className="in-rings__fixed">
                <circle cx="200" cy="200" r="182" />
                <circle cx="200" cy="200" r="152" />
                <circle cx="200" cy="200" r="104" />
              </g>
              <g className="in-rings__spin">
                {RASHIS.map((glyph, i) => {
                  const a = (i / RASHIS.length) * Math.PI * 2 - Math.PI / 2;
                  return (
                    <text
                      key={glyph}
                      x={200 + Math.cos(a) * 167}
                      y={200 + Math.sin(a) * 167}
                      dominantBaseline="central"
                      textAnchor="middle"
                    >
                      {glyph}
                    </text>
                  );
                })}
              </g>
            </svg>

            <div className="in-brand">
              <img
                className="in-brand__logo"
                src="/logo.svg"
                alt=""
                width={92}
                height={92}
                aria-hidden="true"
              />
              <p className="in-brand__name">Aura AI</p>
              <p className="in-brand__offer">Kundli bilkul free</p>
            </div>

            <img
              className="in-cut"
              src={heroBaba}
              alt="Jyotishi holding a janam kundli patra"
              width={660}
              height={726}
              loading="eager"
            />
          </div>

          <div className="in-tag">
            <h1>Puri kundli, bilkul free</h1>
            <ul className="in-intents">
              {INTENTS.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>

            <div className="in-faces" aria-hidden="true">
              <img src={personA} alt="" width={120} height={120} loading="lazy" />
              <img src={personB} alt="" width={120} height={120} loading="lazy" />
              <img src={personC} alt="" width={120} height={120} loading="lazy" />
            </div>
          </div>
        </section>

        <section className="in-block">
          <h2>Ek hi app mein sab kuch</h2>
          <ul className="in-feats">
            {FEATURES.map((f) => (
              <li key={f.label}>
                <span className="in-feats__icon" aria-hidden="true">{f.icon}</span>
                <b>{f.label}</b>
                <span className="in-feats__note">{f.note}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="in-block">
          <h2>Kaise kaam karta hai</h2>
          <ol className="in-steps">
            <li>
              <b>Janam ki detail daaliye</b>
              <span>Date, samay aur jagah. Ek minute se bhi kam lagega.</span>
            </li>
            <li>
              <b>Puri kundli turant</b>
              <span>Lagna, grahon ki sthiti, dasha aur dosha, sab ek saath.</span>
            </li>
            <li>
              <b>Kundli se poochiye</b>
              <span>Jawaab batata hai ki kis ghar ya dasha se nikla hai.</span>
            </li>
          </ol>
        </section>

        <section className="in-block">
          {/* Rating and ratings count read off the live Play Store listing on
              2026-10-01. The download tier is 10L+, confirmed by Dhruv on
              2026-10-02; the listing I read showed the 5L+ tier, so it has
              since crossed. Shown in lakhs here because this page is for
              India; /foreign shows the same figure as 1M+. */}
          <button className="in-stats" type="button" onClick={handleDownload}>
            <span><b>4.4</b><span>Google Play par</span></span>
            <span><b>10L+</b><span>downloads</span></span>
            <span><b>1.7K</b><span>ratings</span></span>
          </button>

          <div className="in-shots" aria-label="Aura AI app ki screens">
            <img src={mockupIntro} alt="Aura AI chat screen" loading="lazy" width={200} height={420} />
            <img src={mockupReports} alt="Personalised report screen" loading="lazy" width={200} height={420} />
            <img src={mockupVoice} alt="Voice chat screen" loading="lazy" width={200} height={420} />
          </div>

          <ul className="in-revs">
            {REVIEWS.map((r) => (
              <li key={r.name}>
                <span className="in-revs__mark" aria-hidden="true">
                  {"“"}
                </span>
                <p>{r.text}</p>
                <div className="in-revs__foot">
                  <span className="in-revs__av" aria-hidden="true">
                    {r.name.charAt(0)}
                  </span>
                  <span className="in-revs__who">
                    <b>{r.name}</b>
                    <span>Google Play</span>
                  </span>
                  <span
                    className="in-revs__rate"
                    aria-label={`${r.stars} out of 5`}
                  >
                    {"★".repeat(r.stars)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <ul className="in-trust">
          <li>
            <span className="in-trust__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="3.4" />
                <path d="M12 3v3.6M12 17.4V21M3 12h3.6M17.4 12H21" />
              </svg>
            </span>
            <b>Asli kundli</b>
          </li>
          <li>
            <span className="in-trust__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M20 14.5a2.5 2.5 0 0 1-2.5 2.5H9l-4 3.2V6.5A2.5 2.5 0 0 1 7.5 4h10A2.5 2.5 0 0 1 20 6.5z" />
                <rect x="9.5" y="9.4" width="6" height="4.4" rx="1" />
                <path d="M10.9 9.4V8.2a1.6 1.6 0 0 1 3.2 0v1.2" />
              </svg>
            </span>
            <b>Baat private rehti hai</b>
          </li>
          <li>
            <span className="in-trust__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
                <path d="M2.5 10h19" />
                <path d="M6 14.4h4" />
              </svg>
            </span>
            <b>Payment safe</b>
          </li>
        </ul>
      </main>

      <div className="in-bar">
        <button className="in-cta" type="button" onClick={handleDownload}>
          Apni FREE kundli banaiye
        </button>
      </div>
    </div>
  );
};

export default KundliPage;
